import {
  doc,
  onSnapshot,
  serverTimestamp,
  increment,
  Timestamp,
  type Firestore,
  type WriteBatch,
} from 'firebase/firestore'
import { firestoreDb } from './firebase'
import type { WorkspaceQuota } from '../types/chat'

export interface ChatQuotaState {
  fallback: boolean
  timezone: string
  dayKey: string
  limit: number
}

// In-memory state keyed by workspaceId to coordinate write batches with current subscription
const quotaStateByWorkspace = new Map<string, ChatQuotaState>()
const activeSubscriberCounts = new Map<string, number>()

export function getWorkspaceQuotaState(workspaceId: string): ChatQuotaState | undefined {
  return quotaStateByWorkspace.get(workspaceId)
}

export function _resetQuotaStateForTesting(): void {
  quotaStateByWorkspace.clear()
  activeSubscriberCounts.clear()
}

export function resolveFallbackChatLimit(plan?: string | null): number {
  if (plan === 'FREE_TIER') return 50
  return -1
}

export function resolveFallbackChatHistoryDays(plan?: string | null): number {
  if (!plan || plan === 'FREE_TIER') return 7
  return -1
}

export function historyCutoff(nowMs: number, days: number, marginMs: number = 10 * 60_000): Timestamp | null {
  if (days === -1) return null
  return Timestamp.fromMillis(nowMs - days * 86_400_000 + marginMs)
}

export function requestChatHistoryUpgrade(workspaceId?: string): void {
  if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
    window.dispatchEvent(
      new CustomEvent('linkhub:paywall-required', {
        detail: {
          featureKey: 'chatHistoryDays',
          ...(workspaceId ? { workspaceId } : {}),
        },
      }),
    )
  }
}

export function dayKeyFromOffset(nowMs: number, utcOffsetMinutes: number): string {
  const shifted = new Date(nowMs + utcOffsetMinutes * 60_000)
  const y = shifted.getUTCFullYear()
  const m = String(shifted.getUTCMonth() + 1).padStart(2, '0')
  const d = String(shifted.getUTCDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

export function msUntilNextOffsetMidnight(nowMs: number, utcOffsetMinutes: number): number {
  const shifted = new Date(nowMs + utcOffsetMinutes * 60_000)
  const msSinceMidnight =
    ((shifted.getUTCHours() * 60 + shifted.getUTCMinutes()) * 60 + shifted.getUTCSeconds()) * 1000 +
    shifted.getUTCMilliseconds()
  const remaining = 86_400_000 - msSinceMidnight
  return remaining > 0 ? remaining : 86_400_000
}


export interface SubscribeChatQuotaOptions {
  db?: Firestore
  now?: () => Date
  onError?: (error: unknown) => void
}

/**
 * Listens to `workspaceEffectiveLimits/{workspaceId}` (sets limit, timezone, utcOffsetMinutes)
 * and `workspaces/{workspaceId}/chatUsage/{dayKey}` (where dayKey is dayKeyFromOffset(now, utcOffsetMinutes)).
 * If the effectiveLimits doc is missing, falls back to reading the workspace doc.
 * Resets cleanly at local midnight.
 */
export function subscribeChatQuota(
  workspaceId: string,
  cb: (quota: WorkspaceQuota) => void,
  options?: SubscribeChatQuotaOptions,
): () => void {
  const db = options?.db ?? firestoreDb
  const getNow = options?.now ?? (() => new Date())

  activeSubscriberCounts.set(workspaceId, (activeSubscriberCounts.get(workspaceId) ?? 0) + 1)

  let unsubUsage: (() => void) | null = null
  let unsubFallbackWorkspace: (() => void) | null = null
  let midnightTimer: ReturnType<typeof setTimeout> | null = null
  let activeDayKey: string | null = null
  let activeTimezone = 'UTC'
  let activeOffsetMinutes = 0
  let activeLimit = -1
  let activeHistoryDays = -1

  const clearMidnightTimer = () => {
    if (midnightTimer) {
      clearTimeout(midnightTimer)
      midnightTimer = null
    }
  }

  const setupUsageListener = (
    dayKey: string,
    timezone: string,
    utcOffsetMinutes: number,
    limit: number,
    historyDays: number,
  ) => {
    if (unsubUsage) {
      unsubUsage()
      unsubUsage = null
    }
    activeDayKey = dayKey
    activeTimezone = timezone
    activeOffsetMinutes = utcOffsetMinutes
    activeLimit = limit
    activeHistoryDays = historyDays

    const usageRef = doc(db, 'workspaces', workspaceId, 'chatUsage', dayKey)
    unsubUsage = onSnapshot(
      usageRef,
      (snapshot) => {
        const docExists = snapshot.exists()
        const usedToday = docExists ? (snapshot.data()?.count ?? 0) : 0
        const isOverQuota = activeLimit !== -1 && usedToday >= activeLimit

        quotaStateByWorkspace.set(workspaceId, {
          fallback: false,
          timezone: activeTimezone,
          dayKey,
          limit: activeLimit,
        })

        cb({
          limit: activeLimit,
          usedToday,
          dayKey,
          isOverQuota,
          historyDays: activeHistoryDays,
        })
      },
      (error) => {
        console.warn('[Firestore listener failed] chatUsage', workspaceId, dayKey, error)
      },
    )

    // Schedule midnight timer
    clearMidnightTimer()
    const msUntilMidnight = msUntilNextOffsetMidnight(getNow().getTime(), activeOffsetMinutes)
    midnightTimer = setTimeout(() => {
      const nextDayKey = dayKeyFromOffset(getNow().getTime(), activeOffsetMinutes)
      if (nextDayKey !== activeDayKey) {
        setupUsageListener(nextDayKey, activeTimezone, activeOffsetMinutes, activeLimit, activeHistoryDays)
      } else {
        // Retry in 1 second if clock hasn't flipped yet
        midnightTimer = setTimeout(() => {
          setupUsageListener(dayKeyFromOffset(getNow().getTime(), activeOffsetMinutes), activeTimezone, activeOffsetMinutes, activeLimit, activeHistoryDays)
        }, 1000)
      }
    }, msUntilMidnight + 50) // Small safety buffer to ensure crossing 00:00:00
  }

  const limitsRef = doc(db, 'workspaceEffectiveLimits', workspaceId)
  const unsubLimits = onSnapshot(
    limitsRef,
    (snapshot) => {
      if (snapshot.exists()) {
        if (unsubFallbackWorkspace) {
          unsubFallbackWorkspace()
          unsubFallbackWorkspace = null
        }
        const data = snapshot.data() as Record<string, unknown>
        const limit = typeof data.chatMessageLimit === 'number' ? data.chatMessageLimit : -1
        const timezone = typeof data.timezone === 'string' && data.timezone.trim() ? data.timezone.trim() : 'UTC'
        const utcOffsetMinutes = typeof data.utcOffsetMinutes === 'number' ? data.utcOffsetMinutes : 0
        const dayKey = dayKeyFromOffset(getNow().getTime(), utcOffsetMinutes)
        const historyDays = typeof data.chatHistoryDays === 'number'
          ? data.chatHistoryDays
          : resolveFallbackChatHistoryDays(typeof data.tier === 'string' ? data.tier : null)

        setupUsageListener(dayKey, timezone, utcOffsetMinutes, limit, historyDays)
      } else {
        // Fallback: effectiveLimits missing
        if (unsubUsage) {
          unsubUsage()
          unsubUsage = null
        }
        clearMidnightTimer()

        if (!unsubFallbackWorkspace) {
          const wsRef = doc(db, 'workspaces', workspaceId)
          unsubFallbackWorkspace = onSnapshot(
            wsRef,
            (wsSnap) => {
              if (!wsSnap.exists()) {
                quotaStateByWorkspace.delete(workspaceId)
                return
              }
              const wsData = wsSnap.data() as Record<string, unknown>
              const plan = typeof wsData.plan === 'string' ? wsData.plan : null
              const limit = resolveFallbackChatLimit(plan)
              const usedToday = typeof wsData.dailyChatsSentCount === 'number' ? wsData.dailyChatsSentCount : 0
              const timezone = typeof wsData.timezone === 'string' && wsData.timezone.trim() ? wsData.timezone.trim() : 'UTC'
              const dayKey = dayKeyFromOffset(getNow().getTime(), 0)
              const isOverQuota = limit !== -1 && usedToday >= limit
              const historyDays = resolveFallbackChatHistoryDays(plan)

              quotaStateByWorkspace.set(workspaceId, {
                fallback: true,
                timezone,
                dayKey,
                limit,
              })

              cb({
                limit,
                usedToday,
                dayKey,
                isOverQuota,
                historyDays,
              })
            },
            (err) => {
              console.warn('[Firestore listener failed] fallback workspace', workspaceId, err)
              options?.onError?.(err)
            },
          )
        }
      }
    },
    (error) => {
      console.warn('[Firestore listener failed] workspaceEffectiveLimits', workspaceId, error)
      options?.onError?.(error)
    },
  )

  return () => {
    unsubLimits()
    if (unsubUsage) unsubUsage()
    if (unsubFallbackWorkspace) unsubFallbackWorkspace()
    clearMidnightTimer()

    const remaining = (activeSubscriberCounts.get(workspaceId) ?? 1) - 1
    if (remaining <= 0) {
      activeSubscriberCounts.delete(workspaceId)
      quotaStateByWorkspace.delete(workspaceId)
    } else {
      activeSubscriberCounts.set(workspaceId, remaining)
    }
  }
}

export interface ChatQuotaWriteOptions {
  db?: Firestore
  now?: () => Date
}

/**
 * Batches the quota step for a message send:
 * - If fallback mode is active: increments `dailyChatsSentCount` on `workspaces/{workspaceId}`.
 * - Otherwise: writes to `workspaces/{workspaceId}/chatUsage/{dayKey}`, creating if new or stepping count if existing.
 */
export function chatQuotaWrite(
  batch: WriteBatch,
  workspaceId: string,
  units: number,
  options?: ChatQuotaWriteOptions,
): void {
  const db = options?.db ?? firestoreDb
  const getNow = options?.now ?? (() => new Date())
  const state = quotaStateByWorkspace.get(workspaceId)

  if (state?.fallback) {
    const wsRef = doc(db, 'workspaces', workspaceId)
    batch.update(wsRef, {
      dailyChatsSentCount: increment(units),
    })
    return
  }

  const dayKey = state?.dayKey ?? dayKeyFromOffset(getNow().getTime(), 0)
  const usageRef = doc(db, 'workspaces', workspaceId, 'chatUsage', dayKey)

  batch.set(
    usageRef,
    {
      count: increment(units),
      updatedAt: serverTimestamp(),
    },
    { merge: true },
  )
}

